import './index.scss'

import { useEffect, useState } from 'react'

import { useAuth } from '@auth/AuthProvider'
import PageTabs from '@components/PageTabs'
import PersonCard from '@components/PersonCard'
import StaffContextMenu from '@components/StaffContextMenu'
import { useChurchDataProvider } from '@data/ChurchDataProvider'
import { ChurchStaff } from '@data/interfaces/church.interfaces'
import StaffMemberModal from './StaffMemberModal'
import DeleteStaffMemberModal from './DeleteStaffMemberModal'

// Matches the context menu's exit animation.
const MENU_EXIT_DURATION_MS = 140;

type StaffMenuState = {
  staff: ChurchStaff;
  x: number;
  y: number;
};

export default function StaffPage() {
  const auth = useAuth();
  const { staff, reloadChurch } = useChurchDataProvider();

  useEffect(() => {
    reloadChurch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keyed per open, so each open starts from the member as they are now. A
  // null member means inviting someone new.
  const [memberForEdit, setMemberForEdit] = useState<ChurchStaff | null>(null);
  const [isMemberModalOpen, setIsMemberModalOpen] = useState(false);
  const [memberModalKey, setMemberModalKey] = useState(0);

  const openMemberModal = (member: ChurchStaff | null) => {
    setMemberForEdit(member);
    setMemberModalKey((currentKey) => currentKey + 1);
    setIsMemberModalOpen(true);
  };

  const [memberForDelete, setMemberForDelete] = useState<ChurchStaff | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);

    window.setTimeout(() => {
      setMemberForDelete(null);
    }, 200);
  };

  // The menu stays mounted through its exit animation, so the selected and
  // displayed states are tracked separately.
  const [selectedMenu, setSelectedMenu] = useState<StaffMenuState | null>(null);
  const [displayedMenu, setDisplayedMenu] = useState<StaffMenuState | null>(null);
  const [isClosingMenu, setIsClosingMenu] = useState(false);

  useEffect(() => {
    if (selectedMenu) {
      setDisplayedMenu(selectedMenu);
      setIsClosingMenu(false);
      return;
    }

    if (!displayedMenu) {
      setIsClosingMenu(false);
      return;
    }

    setIsClosingMenu(true);

    const timeout = window.setTimeout(() => {
      setDisplayedMenu(null);
      setIsClosingMenu(false);
    }, MENU_EXIT_DURATION_MS);

    return () => window.clearTimeout(timeout);
  }, [selectedMenu, displayedMenu]);

  const sortedStaff = [...staff].sort((one, two) => one.name.localeCompare(two.name));

  return <div className='staff-page'>
    <PageTabs
      title='Staff Members'
      tabs={[]}
      buttons={[
        {
          icon: 'add',
          label: 'Invite Staff Member',
          onClick: () => openMemberModal(null)
        }
      ]} />

    <StaffMemberModal
      key={`staff-member-${memberModalKey}`}
      isOpen={isMemberModalOpen}
      staff={memberForEdit}
      close={() => setIsMemberModalOpen(false)}
      onSaved={() => {
        setIsMemberModalOpen(false);
        reloadChurch();
      }}
    />

    <DeleteStaffMemberModal
      key={memberForDelete?.id ?? 'delete-staff-member'}
      isOpen={isDeleteModalOpen}
      staff={memberForDelete}
      close={closeDeleteModal}
      onDeleted={() => {
        closeDeleteModal();
        reloadChurch();
      }}
    />

    {displayedMenu ? <StaffContextMenu
      x={displayedMenu.x}
      y={displayedMenu.y}
      isClosing={isClosingMenu}
      onRequestClose={() => setSelectedMenu(null)}
      canDelete={displayedMenu.staff.id !== auth.user?.id}
      onEdit={() => {
        setSelectedMenu(null);
        openMemberModal(displayedMenu.staff);
      }}
      onDelete={() => {
        setSelectedMenu(null);
        setMemberForDelete(displayedMenu.staff);
        setIsDeleteModalOpen(true);
      }}
    /> : null}

    <div className='staff'>
      {sortedStaff.map((member) => {
        return <PersonCard
          key={member.id}
          name={member.name}
          email={[...member.roles].sort().join(', ')}
          onClick={(event) => {
            setSelectedMenu({
              staff: member,
              x: event.clientX + window.scrollX,
              y: event.clientY + window.scrollY
            });
          }}
        />
      })}
    </div>
  </div>
}
